import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useAuth } from "../auth/AuthContext";
import { PlanBadge, RoleBadge } from "./PlanBadge";
import { Icon } from "./Icons";

const TOPBAR_MOBILE_MAX_PX = 1024;

/** Decorative icon for the Build nav link (stacked layers → deck). */
function NavBuildIcon() {
  return <Icon name="layers" className="nav-link-build-icon" />;
}

function NavBloomyIcon() {
  return (
    <img
      className="nav-link-bloomy-icon"
      src="/bloomy.png"
      alt=""
      width={22}
      height={22}
      decoding="async"
      draggable={false}
      aria-hidden
    />
  );
}

function navCardsPathActive(pathname: string) {
  return (
    pathname === "/analyze" || pathname === "/collection" || pathname === "/decks"
  );
}

type MenuAnchor = { top: number; left: number; minWidth: number };

function NavCardsMenu() {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<MenuAnchor | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  const updateAnchor = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const minWidth = Math.max(176, r.width);
    let left = r.left;
    const maxLeft = window.innerWidth - minWidth - 10;
    if (left > maxLeft) left = Math.max(8, maxLeft);
    if (left < 8) left = 8;
    setAnchor({ top: r.bottom + 6, left, minWidth });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setAnchor(null);
      return;
    }
    updateAnchor();
  }, [open, updateAnchor, location.pathname]);

  useEffect(() => {
    if (!open) return;
    const on = () => updateAnchor();
    window.addEventListener("resize", on);
    window.addEventListener("scroll", on, true);
    return () => {
      window.removeEventListener("resize", on);
      window.removeEventListener("scroll", on, true);
    };
  }, [open, updateAnchor]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = ref.current?.contains(target);
      const inPanel = panelRef.current?.contains(target);
      if (!inTrigger && !inPanel) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const cardsMenuActive = navCardsPathActive(location.pathname);
  const panelStyle = anchor
    ? {
        top: anchor.top,
        left: anchor.left,
        minWidth: anchor.minWidth,
      }
    : undefined;

  return (
    <div className="nav-dropdown" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className={
          "nav-link nav-dropdown-trigger" +
          (cardsMenuActive ? " active" : "") +
          (open ? " is-open" : "")
        }
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => {
          if (!open) updateAnchor();
          setOpen((v) => !v);
        }}
      >
        Cards
        <span className="nav-dropdown-caret" aria-hidden />
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            className={"nav-dropdown-panel" + (anchor ? " nav-dropdown-panel--fixed" : "")}
            id="nav-cards-menu"
            role="menu"
            aria-label="Cards"
            style={panelStyle}
          >
            <NavLink
              to="/collection"
              className={({ isActive }) => "nav-dropdown-item" + (isActive ? " active" : "")}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              My Collection
            </NavLink>
            <NavLink
              to="/analyze"
              className={({ isActive }) => "nav-dropdown-item" + (isActive ? " active" : "")}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              Analyze
            </NavLink>
            <NavLink
              to="/decks"
              className={({ isActive }) => "nav-dropdown-item" + (isActive ? " active" : "")}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              Decks
            </NavLink>
          </div>,
          document.body,
        )}
    </div>
  );
}

export function Navbar() {
  const { firebaseUser, profile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close the drawer whenever the route changes or the viewport goes wide.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > TOPBAR_MOBILE_MAX_PX) setOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Prevent body scroll while drawer is open on mobile.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  const navLinksPublic = (
    <>
      <NavLink to="/" end className="nav-link">Home</NavLink>
      <NavLink to="/community" className="nav-link">Community</NavLink>
      <NavLink to="/about" className="nav-link">About</NavLink>
      <NavLink to="/pricing" className="nav-link">Pricing</NavLink>
    </>
  );

  const navLinksAuthedDesktop = (
    <>
      <NavLink to="/" end className="nav-link">Home</NavLink>
      <NavLink to="/community" className="nav-link">Community</NavLink>
      <NavLink to="/bloomy" className="nav-link nav-link-bloomy">
        <NavBloomyIcon />
        Bloomy
      </NavLink>
      <NavCardsMenu />
      <NavLink to="/build" className="nav-link nav-link-build">
        <NavBuildIcon />
        Build
      </NavLink>
    </>
  );

  const navLinksAuthedMobile = (
    <>
      <NavLink to="/" end className="nav-link">Home</NavLink>
      <NavLink to="/build" className="nav-link nav-link-build">
        <NavBuildIcon />
        Build
      </NavLink>
      <NavLink to="/collection" className="nav-link">My Collection</NavLink>
      <NavLink to="/analyze" className="nav-link">Analyze</NavLink>
      <NavLink to="/decks" className="nav-link">Decks</NavLink>
      <NavLink to="/community" className="nav-link">Community</NavLink>
      <NavLink to="/bloomy" className="nav-link nav-link-bloomy">
        <NavBloomyIcon />
        Bloomy
      </NavLink>
      {profile?.isAdmin && <NavLink to="/admin" className="nav-link">Admin</NavLink>}
    </>
  );

  const actions = firebaseUser ? (
    <>
      {profile?.isAdmin && <NavLink to="/admin" className="nav-link nav-link-admin">Admin</NavLink>}
      <NavLink to="/downloads" className="topbar-btn">
        <Icon name="download" size={16} />
        Downloads
      </NavLink>
      <Link to="/account" className="account-link">
        <span className="account-label">My Account</span>
        <PlanBadge plan={profile?.plan} />
        <RoleBadge role={profile?.role} />
      </Link>
      <button className="topbar-btn" onClick={handleLogout}>
        <Icon name="login" size={16} />
        Sign out
      </button>
    </>
  ) : (
    <>
      <NavLink to="/downloads" className="topbar-btn">
        <Icon name="download" size={16} />
        Downloads
      </NavLink>
      <Link to="/login" className="topbar-btn"><Icon name="login" size={16} />Sign in</Link>
      <Link to="/register" className="topbar-btn primary"><Icon name="sparkles" size={16} />Sign up</Link>
    </>
  );

  return (
    <>
      <div className="topbar-wrap">
        <header className="topbar">
        <nav className="nav-links" aria-label="Main pages">
          {firebaseUser ? navLinksAuthedDesktop : navLinksPublic}
        </nav>

        <div className="brand">
          <Link to="/" className="brand-link" title="Foil Bloom — home">
            <picture className="brand-logo-picture">
              <source
                type="image/png"
                media="(max-width: 520px)"
                srcSet="/foilbloom-icon.png"
              />
              <img
                className="brand-logo-img"
                src="/foilbloom-icon-text.png"
                alt="Foil Bloom"
                width={320}
                height={48}
                decoding="async"
                draggable={false}
              />
            </picture>
          </Link>
        </div>

        <div className="nav-actions">{actions}</div>

        <button
          type="button"
          className="nav-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span />
        </button>
        </header>
      </div>

      <div className={`mobile-nav${open ? " open" : ""}`} aria-hidden={!open}>
        <div className="nav-links" style={{ flexDirection: "column", gap: 2 }}>
          {firebaseUser ? navLinksAuthedMobile : navLinksPublic}
        </div>
        <div className="nav-actions">{actions}</div>
      </div>
    </>
  );
}
